/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CardHeader from '../../../components/CardHeader';
import ItemShimmer from '../../../components/Shimmer/itemShimmer';
import Pagination from '../../../components/Pagination';
import EmptyAddScreen from '../../../components/EmptyAddScreen';
import ConfirmationPrompt from '../../../components/ConfirmationPrompt';
import AnimatedModel from '../../../components/Animation/AnimatedModel';
import Toast from '../../../components/Toast';
import CustomeDatePicker from '../../../components/DatePicker';
import FeeStructureDropdown from '../../../components/FeeStructureDropdown';
import GlobalContext from '../../../components/Context/GloablContext';
import { dataService } from '../../../services/data.services';
import { GetBranchId } from '../../../CommonMethods/GetBranchId';
import { CognitoData } from '../../../CommonMethods/CognitoData';
import { HomeworkList, NoticesList } from './ClassRoomSlice';
import HomeworkTable from './Homework/HomeworkTable';
import UpdatesTable from './Updates/UpdatesTable';
import moment from 'moment';
import { fetchNoticeTypeOptions } from './noticeTypeUtils';
import Loader from "../../../components/Loader";
import { isAcademicYearCompleted } from '../../HolidayCalendar/CommonMethods';
import {
    filterAcademicYearsByAssociations,
    filterClassesByAssociations,
    getSectionsByAssociations,
    getStaffAssociationsForAcademicYears,
    getStaffAssociationContext,
    isAdminCommunicationRole,
} from './associationUtils';

const TABS = { HOMEWORK: 'homework', UPDATES: 'updates' };

export default function ClassDiary() {
    const { isSideNavBar, setToastmessage, isInactiveBranch } = useContext(GlobalContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams({ type: TABS.HOMEWORK, page_number: 1 });
    const searchParamsString = searchParams.toString();
    const activeTab = searchParams.get('type') || TABS.HOMEWORK;

    const [list, setList] = useState({});
    const [loading, setLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(false);
    const [emptyScreenMessage, setEmptyScreenMessage] = useState('');
    const [confirmationState, setConfirmationState] = useState({});
    const [loadingActivation, setLoadingActivation] = useState(false);
    const [toastErrorMessage, setLocalToastErrorMessage] = useState('');
    const [userType, setUserType] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [staffAssociations, setStaffAssociations] = useState([]);
    const [userContextReady, setUserContextReady] = useState(false);
    const [filtersReady, setFiltersReady] = useState(false);
    const branchIdRef = useRef('');
    const classesCacheRef = useRef(null);
    const associationsByAcademicYearRef = useRef(new Map());
    const sectionsByAcademicYearClassRef = useRef(new Map());

    // Filter state
    const [filterAYs, setFilterAYs] = useState([]);
    const [filterClasses, setFilterClasses] = useState([]);
    const [filterSections, setFilterSections] = useState([]);
    const [loadingFilterAYs, setLoadingFilterAYs] = useState(true);
    const [loadingFilterClasses, setLoadingFilterClasses] = useState(false);
    const [loadingFilterSections, setLoadingFilterSections] = useState(false);
    const [applyingFilters, setApplyingFilters] = useState(false);
    const [filterAY, setFilterAY] = useState({});
    const [filterClass, setFilterClass] = useState({});
    const [filterSection, setFilterSection] = useState({});
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterNoticeType, setFilterNoticeType] = useState('');
    const [noticeTypeOptions, setNoticeTypeOptions] = useState([{ value: '', label: 'All Types' }]);

    const isHomework = activeTab === TABS.HOMEWORK;
    const today = moment().format('YYYY-MM-DD');

    const getDefaultAcademicYear = useCallback((academicYears = []) => {
        if (!academicYears.length) return null;
        return academicYears.find((ay) => String(ay?.status || '').toLowerCase() === 'ongoing') || academicYears[0];
    }, []);

    const getAcademicYearStartDate = useCallback((academicYear) => {
        if (!academicYear?.start_date) return '';
        return String(academicYear.start_date).split('T')[0];
    }, []);

    const getAcademicYearToDate = useCallback((academicYear) => {
        const rawAcademicYearEndDate = academicYear?.end_date
            || academicYear?.endDate
            || academicYear?.academic_year_end_date
            || academicYear?.to_date
            || '';

        if (!rawAcademicYearEndDate) {
            return today;
        }

        const academicYearEndDate = String(rawAcademicYearEndDate).split('T')[0];

        if (academicYearEndDate && academicYearEndDate < today) {
            return academicYearEndDate;
        }
        return today;
    }, [today]);

    const getDefaultDateFrom = useCallback((academicYear, homeworkMode) => (
        homeworkMode ? today : getAcademicYearStartDate(academicYear)
    ), [getAcademicYearStartDate, today]);

    const getDefaultDateTo = useCallback((academicYear, homeworkMode) => (
        homeworkMode ? today : getAcademicYearToDate(academicYear)
    ), [getAcademicYearToDate, today]);

    const normalizePickerDate = useCallback((date) => {
        if (!date) return '';
        if (typeof date.format === 'function') {
            return date.format('YYYY-MM-DD');
        }
        return moment(date).format('YYYY-MM-DD');
    }, []);

    const getCachedBranchId = useCallback(async () => {
        if (branchIdRef.current) return branchIdRef.current;
        const branch_id = await GetBranchId();
        branchIdRef.current = branch_id;
        return branch_id;
    }, []);

    const getCachedClasses = useCallback(async () => {
        if (classesCacheRef.current) return classesCacheRef.current;
        const branch_id = await getCachedBranchId();
        const res = await dataService.GetAPI(`classes?branch_id=${branch_id}&sort=${encodeURIComponent('status+asc')}`);
        const allClasses = (res.data?.data?.data || []).map((classItem) => {
            if (classItem?.status === false && !String(classItem?.name || '').includes('(Inactive)')) {
                return {
                    ...classItem,
                    name: `${classItem?.name || ''} (Inactive)`.trim(),
                };
            }
            return classItem;
        });
        classesCacheRef.current = allClasses;
        return allClasses;
    }, [getCachedBranchId]);

    const getCachedAssociationsForAcademicYear = useCallback(async (ay_id) => {
        if (!ay_id) return [];
        if (associationsByAcademicYearRef.current.has(ay_id)) {
            return associationsByAcademicYearRef.current.get(ay_id);
        }
        const branch_id = await getCachedBranchId();
        const assocRes = await dataService.GetAPI(`associations?branch_id=${branch_id}&academic_year_id=${ay_id}&filter=true`);
        const assocData = assocRes.data?.data?.data || [];
        associationsByAcademicYearRef.current.set(ay_id, assocData);
        return assocData;
    }, [getCachedBranchId]);

    const getCachedSectionsForAcademicYearClass = useCallback(async (ay_id, class_id) => {
        if (!ay_id || !class_id) return [];
        const cacheKey = `${ay_id}:${class_id}`;
        if (sectionsByAcademicYearClassRef.current.has(cacheKey)) {
            return sectionsByAcademicYearClassRef.current.get(cacheKey);
        }
        const assocData = await getCachedAssociationsForAcademicYear(ay_id);
        const filteredAssocData = assocData.filter((item) => item.class_id === class_id);
        const allSections = [...new Map(
            filteredAssocData.map((item) => [item.section_id, {
                _id: item.section_id,
                name: item.section_status === false && !String(item.section_name || '').includes('(Inactive)')
                    ? `${item.section_name || ''} (Inactive)`.trim()
                    : item.section_name,
                section_id: item.section_id,
                section_name: item.section_name,
                section_status: item.section_status,
            }])
        ).values()];
        sectionsByAcademicYearClassRef.current.set(cacheKey, allSections);
        return allSections;
    }, [getCachedAssociationsForAcademicYear]);

    const getAcademicYearsForUser = useCallback((allAcademicYears, type, associations = []) => {
        if (isAdminCommunicationRole(type)) return allAcademicYears;
        return filterAcademicYearsByAssociations(allAcademicYears, associations);
    }, []);

    const handleTabSwitch = (tab) => {
        setList({});
        setFirstLoading(false);
        setEmptyScreenMessage('');
        const nextIsHomework = tab === TABS.HOMEWORK;
        const nextDateFrom = getDefaultDateFrom(filterAY, nextIsHomework);
        const nextDateTo = getDefaultDateTo(filterAY, nextIsHomework);
        setFilterDateFrom(nextDateFrom);
        setFilterDateTo(nextDateTo);
        setFilterNoticeType('');
        const params = { type: tab, page_number: 1 };
        if (filterAY?._id) params.academic_year_id = filterAY._id;
        if (filterClass?._id) params.class_id = filterClass._id;
        if (filterSection?._id) params.section_id = filterSection._id;
        if (nextDateFrom) params.date_from = nextDateFrom;
        if (nextDateTo) params.date_to = nextDateTo;
        setSearchParams(params);
    };

    const getUserType = useCallback(async () => {
        const user = await CognitoData();
        const type = user.idToken.payload['custom:user_type'];
        const email = user.idToken.payload['email'];
        setUserType(type);
        setUserEmail(email);
        const branch_id = await getCachedBranchId();
        try {
            const dynamicNoticeTypes = await fetchNoticeTypeOptions(branch_id);
            setNoticeTypeOptions([{ value: '', label: 'All Types' }, ...dynamicNoticeTypes]);
        } catch {
            setNoticeTypeOptions([{ value: '', label: 'All Types' }]);
        }
        setLoadingFilterAYs(true);
        try {
            const res = await dataService.GetAPI(`academic-years?branch_id=${branch_id}&filter=true&status=ongoing,upcoming,completed`);
            const allAcademicYears = res.data?.data?.data || [];
            if (isAdminCommunicationRole(type)) {
                setStaffAssociations([]);
                setFilterAYs(allAcademicYears);
            } else {
                const allClasses = await getCachedClasses();
                const context = await getStaffAssociationContext(branch_id, email);
                const assignedAssociations = await getStaffAssociationsForAcademicYears(
                    branch_id,
                    allAcademicYears,
                    context.userData || {},
                );
                setStaffAssociations(assignedAssociations);
                const staffAcademicYears = getAcademicYearsForUser(allAcademicYears, type, assignedAssociations)
                    .filter((academicYear) =>
                        filterClassesByAssociations(allClasses, assignedAssociations, academicYear._id).length > 0
                    );
                setFilterAYs(staffAcademicYears);
            }
        } finally {
            setLoadingFilterAYs(false);
            setUserContextReady(true);
        }
    }, [getAcademicYearsForUser, getCachedBranchId]);

    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            const branch_id = await GetBranchId();
            const params = Object.fromEntries(searchParams);
            delete params.type;
            const queryString = new URLSearchParams(params).toString();
            const endpoint = isHomework
                ? `notice-board/homework?branch_id=${branch_id}&${queryString}`
                : `notice-board/updates?branch_id=${branch_id}&${queryString}`;

            const action = isHomework ? HomeworkList : NoticesList;
            dispatch(action(endpoint)).then((response) => {
                const data = response?.payload?.data;
                if (data?.status === 200) {
                    const listData = isHomework ? (data.data || {}) : (data.data || {});
                    setList(listData);
                    const listItems = isHomework ? (listData.homeworks || []) : (listData.data || []);
                    if (listItems.length === 0 && listData.total_count === 0 && !searchParams.get('filter')) {
                        setFirstLoading(true);
                    } else {
                        setEmptyScreenMessage('No matching records found.');
                    }
                } else {
                    setEmptyScreenMessage('No data found.');
                }
                setLoading(false);
                setApplyingFilters(false);
            });
        } catch (error) {
            setLoading(false);
            setEmptyScreenMessage('No data found.');
            setApplyingFilters(false);
        }
    }, [searchParamsString, isHomework]);

    const fetchFilterClasses = useCallback(async (ay_id) => {
        try {
            setLoadingFilterClasses(true);
            const allClasses = await getCachedClasses();
            if (isAdminCommunicationRole(userType)) {
                setFilterClasses(allClasses);
                return allClasses;
            }
            const filteredClasses = filterClassesByAssociations(allClasses, staffAssociations, ay_id);
            setFilterClasses(filteredClasses);
            return filteredClasses;
        } catch {
            setFilterClasses([]);
            return [];
        } finally {
            setLoadingFilterClasses(false);
        }
    }, [getCachedClasses, staffAssociations, userType]);

    const fetchFilterSections = useCallback(async (class_id, ay_id) => {
        try {
            setLoadingFilterSections(true);
            if (isAdminCommunicationRole(userType)) {
                const allSections = await getCachedSectionsForAcademicYearClass(ay_id, class_id);
                setFilterSections(allSections);
                return allSections;
            }
            const filteredSections = getSectionsByAssociations(staffAssociations, ay_id, class_id);
            setFilterSections(filteredSections);
            return filteredSections;
        } catch {
            setFilterSections([]);
            return [];
        } finally {
            setLoadingFilterSections(false);
        }
    }, [getCachedSectionsForAcademicYearClass, staffAssociations, userType]);

    const handleFilterAY = async (ay) => {
        setFilterAY(ay);
        setFilterClass({});
        setFilterSection({});
        setFilterClasses([]);
        setFilterSections([]);
        const nextDateFrom = filterDateFrom || getDefaultDateFrom(ay, isHomework);
        const nextDateTo = isHomework
            ? (filterDateFrom || getDefaultDateTo(ay, isHomework))
            : (filterDateTo || getDefaultDateTo(ay, isHomework));
        setFilterDateFrom(nextDateFrom);
        setFilterDateTo(nextDateTo);
        setLoadingFilterSections(false);

        if (!ay?._id) {
            setLoadingFilterClasses(false);
            return;
        }

        const classes = await fetchFilterClasses(ay._id);
        if (classes.length > 0) {
            const firstClass = classes[0];
            setFilterClass(firstClass);
            await fetchFilterSections(firstClass._id, ay._id);
            return;
        }

        setFilterSection({});
        setFilterSections([]);
    };

    const handleFilterClass = async (cls) => {
        if (!cls?._id) {
            setFilterClass({});
            setFilterSection({});
            setFilterSections([]);
            setLoadingFilterSections(false);
            return;
        }
        setFilterClass(cls);
        setFilterSection({});
        setFilterSections([]);
        if (filterAY?._id) {
            await fetchFilterSections(cls._id, filterAY._id);
        }
    };

    const handleFilterSection = (sec) => {
        if (!sec?._id) {
            setFilterSection({});
            return;
        }
        setFilterSection(sec);
    };

    const handleFilterDateFromSelect = (id, date) => {
        setFilterDateFrom(normalizePickerDate(date));
    };

    const handleFilterDateToSelect = (id, date) => {
        setFilterDateTo(normalizePickerDate(date));
    };

    const handleHomeworkDateSelect = (id, date) => {
        const formattedDate = normalizePickerDate(date);
        setFilterDateFrom(formattedDate);
        setFilterDateTo(formattedDate);
    };

    const handleFilterNoticeType = (key, value) => {
        setFilterNoticeType(value?.value || '');
    };

    const handleFetchFilters = () => {
        const params = { type: activeTab, page_number: 1 };
        if (filterAY?._id) params.academic_year_id = filterAY._id;
        if (filterClass?._id) params.class_id = filterClass._id;
        if (filterSection?._id) params.section_id = filterSection._id;
        if (isHomework) {
            if (filterDateFrom) {
                params.date_from = filterDateFrom;
                params.date_to = filterDateFrom;
            }
        } else {
            if (filterDateFrom) params.date_from = filterDateFrom;
            if (filterDateTo) params.date_to = filterDateTo;
        }
        if (!isHomework && filterNoticeType) params.notice_type = filterNoticeType;
        const nextSearchParams = new URLSearchParams(params).toString();
        if (nextSearchParams === searchParamsString) {
            fetchList();
            return;
        }
        setApplyingFilters(true);
        setSearchParams(params);
    };

    useEffect(() => {
        if (userContextReady) return;
        getUserType();
    }, [getUserType, userContextReady]);

    useEffect(() => {
        let isMounted = true;

        const syncFiltersFromParams = async () => {
            if (!userContextReady) return;
            if (isMounted) {
                setFiltersReady(false);
            }
            const academicYearId = searchParams.get('academic_year_id') || '';
            const classId = searchParams.get('class_id') || '';
            const sectionId = searchParams.get('section_id') || '';

            setFilterDateFrom(searchParams.get('date_from') || '');
            setFilterDateTo(searchParams.get('date_to') || '');
            setFilterNoticeType(searchParams.get('notice_type') || '');

            if (!academicYearId) {
                const defaultAY = getDefaultAcademicYear(filterAYs);
                if (!defaultAY || !isMounted) return;
                setFilterAY(defaultAY);
                const defaultDateFrom = getDefaultDateFrom(defaultAY, isHomework);
                const defaultDateTo = getDefaultDateTo(defaultAY, isHomework);
                setFilterDateFrom(defaultDateFrom);
                setFilterDateTo(defaultDateTo);
                const params = Object.fromEntries(searchParams);
                params.academic_year_id = defaultAY._id;
                params.date_from = defaultDateFrom;
                params.date_to = defaultDateTo;
                params.page_number = 1;
                setSearchParams(params);
                return;
            }

            const selectedAY = filterAYs.find((ay) => ay._id === academicYearId) || {};
            if (isMounted) {
                setFilterAY(selectedAY);
                if (!searchParams.get('date_from')) {
                    setFilterDateFrom(getDefaultDateFrom(selectedAY, isHomework));
                } else {
                    setFilterDateFrom(searchParams.get('date_from') || '');
                }
                setFilterDateTo(searchParams.get('date_to') || getDefaultDateTo(selectedAY, isHomework));
            }

            const classes = await fetchFilterClasses(academicYearId);
            if (!isMounted) return;

            if (!classId) {
                const defaultClass = classes[0] || {};
                setFilterClass(defaultClass);
                setFilterSection({});
                if (defaultClass?._id) {
                    await fetchFilterSections(defaultClass._id, academicYearId);
                    if (!isMounted) return;
                } else {
                    setFilterSections([]);
                }
                const params = Object.fromEntries(searchParams);
                if (defaultClass?._id) {
                    params.class_id = defaultClass._id;
                } else {
                    delete params.class_id;
                }
                delete params.section_id;
                params.page_number = 1;
                setSearchParams(params);
                return;
            }

            const selectedClass = classes.find((cls) => cls._id === classId) || {};
            setFilterClass(selectedClass);

            const sections = await fetchFilterSections(classId, academicYearId);
            if (!isMounted) return;

            if (!sectionId) {
                setFilterSection({});
                setFiltersReady(true);
                return;
            }

            const selectedSection = sections.find((sec) => sec._id === sectionId) || {};
            setFilterSection(selectedSection);
            setFiltersReady(true);
        };

        syncFiltersFromParams();

        return () => {
            isMounted = false;
        };
    }, [searchParamsString, filterAYs, fetchFilterClasses, fetchFilterSections, getDefaultAcademicYear, getDefaultDateFrom, getDefaultDateTo, isHomework, userContextReady]);
    

    useEffect(() => {
        if (!filtersReady) return;
        fetchList();
    }, [fetchList, filtersReady]);

    const handleOnClickPageNumber = (pageNum) => {
        const params = Object.fromEntries(searchParams);
        setSearchParams({ ...params, page_number: pageNum.selected + 1 });
    };

    const handleNavigateToAdd = () => {
        if (isAcademicYearCompleted(filterAY)) {
            setLocalToastErrorMessage(`AY Completed, ${isHomework ? 'Homework' : 'Updates'} cannot be added`);
            return;
        }

        const params = new URLSearchParams();
        if (filterAY?._id) params.set('academic_year_id', filterAY._id);
        if (filterClass?._id) params.set('class_id', filterClass._id);
        if (filterSection?._id) params.set('section_id', filterSection._id);
        if (isHomework && filterDateFrom) {
            params.set('date_from', filterDateFrom);
            params.set('date_to', filterDateFrom);
        }
        if (!isHomework && filterNoticeType) params.set('notice_type', filterNoticeType);
        const query = params.toString();
        navigate(
            `${isHomework ? '/noticeboard/classroom/homework/add' : '/noticeboard/classroom/updates/add'}${query ? `?${query}` : ''}`,
            {
                state: {
                    classroomContext: {
                        academicYear: filterAY?._id ? filterAY : {},
                        classItem: filterClass?._id ? filterClass : {},
                        section: filterSection?._id ? filterSection : {},
                        academicYears: filterAYs,
                        classes: filterClasses,
                        allClasses: classesCacheRef.current || filterClasses,
                        sections: filterSections,
                        staffAssociations,
                        userType,
                        userEmail,
                        date: isHomework ? filterDateFrom : '',
                    },
                },
            }
        );
    };

    const handleDelete = async () => {
        setLoadingActivation(true);
        try {
            const branch_id = await GetBranchId();
            const endpoint = isHomework
                ? `notice-board/homework/${confirmationState.item._id}?branch_id=${branch_id}`
                : `notice-board/updates/${confirmationState.item._id}?branch_id=${branch_id}`;
            const response = await dataService.DeleteAPI(endpoint);
            setLoadingActivation(false);
            if (response?.data?.status === 200) {
                setToastmessage(response.data.message || 'Deleted successfully');
                fetchList();
            } else {
                setLocalToastErrorMessage(response?.data?.message || 'Something went wrong');
            }
            setConfirmationState({});
        } catch (error) {
            setLoadingActivation(false);
            setConfirmationState({});
            setLocalToastErrorMessage(error?.response?.data?.message || 'Something went wrong');
        }
    };

    const isAdminRole = ['institution-admin', 'institution-user', 'branch-user'].includes(userType);

    const canDelete = (item) => {
        const entryDate = item.date;
        if (entryDate < today) return false;
        if (isAdminRole) return true;
        // Homework list delete removes entire batch entry — admin only per spec §9a
        if (isHomework) return false;
        // Updates: staff can delete their own entries per spec §9b
        return item.created_by === userEmail;
    };

    const canEdit = (item) => {
        const entryDate = item.date;
        if (entryDate < today) return false;
        if (isAdminRole) return true;
        return item.created_by === userEmail;
    };

    const academicYearValue = filterAY?.name || 'Select Academic Year';
    const classValue = filterClass?.name || 'Select Class';
    const sectionValue = filterSection?.name || (filterSections.length > 0 ? 'All Sections' : 'Select Section');
    const noticeTypeValue = noticeTypeOptions.find((item) => item.value === filterNoticeType)?.label || 'All Types';
    const classOptions = filterClasses;
    const sectionOptions = filterSections.length > 0 ? [{ name: 'All Sections' }, ...filterSections] : filterSections;
    const isAddBlocked = isAcademicYearCompleted(filterAY);
    const noticeTypeDropdownOptions = noticeTypeOptions.map((item) => ({
        ...item,
        name: item.label,
    }));

    return (
        <>
            <div className={`w-full main-screenInst overflow-x-hidden max-h-screen min-h-screen relative ${!isSideNavBar ? 'w-[270px]' : ''}`}>
                <div className={`mainDataCntnr ${!isSideNavBar ? 'normal' : 'wide'}`}>
                    <div className="mainDataCntnrTop shadow-s13" style={{ position: 'sticky', WebkitPosition: 'sticky' }}>
                        <h5 className="pageHeader">Classroom</h5>
                        {toastErrorMessage !== '' && (
                            <Toast message={toastErrorMessage} type="error" setToastmessage={setLocalToastErrorMessage} />
                        )}
                    </div>

                    <div className="pb-10 relative">
                        <ul className="mr-4 ml-4 flex list-none flex-row flex-wrap border-b border-[#B3BBC1] pl-9 
                            pt-6 bg-[#fff] sticky top-[64px] left-0 -mx-1" role="tablist">
                            <li>
                                <p
                                    id="homework_tab"
                                    onClick={() => handleTabSwitch(TABS.HOMEWORK)}
                                    className={`${activeTab === TABS.HOMEWORK ? 'selected' : ''} mr-[126px] tab-element`}
                                >
                                    Homework
                                </p>
                            </li>
                            <li>
                                <p
                                    id="updates_tab"
                                    onClick={() => handleTabSwitch(TABS.UPDATES)}
                                    className={`${activeTab === TABS.UPDATES ? 'selected' : ''} tab-element`}
                                >
                                    Updates
                                </p>
                            </li>
                        </ul>
                        <div className="tabbedPgListDiv_holidays">
                            {/* Filter row */}
                            <div className={`grid grid-cols-1 ${isHomework ? 'md:grid-cols-6' : 'md:grid-cols-8'} gap-2 my-2 items-end border p-2 rounded sticky top-0 z-20 bg-white`}>
                                {/* Academic Year */}
                                <div className="flex flex-col">
                                    <p className="mb-1 inline-flex">Academic Year<span class="req-fld-astrk">*</span></p>
                                    <FeeStructureDropdown
                                        dropDownName="academic_year"
                                        id="academic_year"
                                        test_id="academic_year"
                                        value={academicYearValue}
                                        placeHolder="Select Academic Year"
                                        options={filterAYs}
                                        handleOnchangeInput={(key, value) => handleFilterAY(value)}
                                        loading={loadingFilterAYs}
                                        buttonDisabled={loadingFilterAYs}
                                    />
                                </div>

                                {/* Class */}
                                <div className="flex flex-col shrink-0">
                                    <p className="text-[#031624] mb-1">Class<span class="req-fld-astrk">*</span></p>
                                    <FeeStructureDropdown
                                        dropDownName="class_name"
                                        value={classValue}
                                        placeHolder="Select Class"
                                        options={classOptions}
                                        handleOnchangeInput={(key, value) => handleFilterClass(value)}
                                        loading={loadingFilterClasses}
                                        buttonDisabled={!filterAY._id || loadingFilterClasses}
                                        applyBGForDisabled={true}
                                    />
                                </div>

                                {/* Section */}
                                <div className="flex flex-col shrink-0">
                                    <p className="text-[#031624] mb-1">Section</p>
                                    <FeeStructureDropdown
                                        dropDownName="section"
                                        value={sectionValue}
                                        placeHolder="Select Section"
                                        options={sectionOptions}
                                        handleOnchangeInput={(key, value) => handleFilterSection(value)}
                                        loading={loadingFilterSections}
                                        buttonDisabled={!filterClass._id || loadingFilterSections}
                                        applyBGForDisabled={true}
                                    />
                                </div>

                                {isHomework ? (
                                    <>
                                        <div className="shrink-0">
                                            <p className="text-[#031624] mb-1">Date</p>
                                            <CustomeDatePicker
                                                id="filter_homework_date"
                                                format="MMM DD, YYYY"
                                                state={{ filter_homework_date: filterDateFrom || '' }}
                                                placeholder="Select Date"
                                                imageClass={true}
                                                handleSelect={handleHomeworkDateSelect}
                                                setdateError={() => {}}
                                                setToastErrorMessage={setLocalToastErrorMessage}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="shrink-0">
                                            <p className="text-[#031624] mb-1">From</p>
                                            <CustomeDatePicker
                                                id="filter_date_from"
                                                format="MMM DD, YYYY"
                                                state={{ filter_date_from: filterDateFrom || '' }}
                                                placeholder="Select From Date"
                                                imageClass={true}
                                                handleSelect={handleFilterDateFromSelect}
                                                setdateError={() => {}}
                                                setToastErrorMessage={setLocalToastErrorMessage}
                                            />
                                        </div>
                                        <div className="shrink-0">
                                            <p className="text-[#031624] mb-1">To</p>
                                            <CustomeDatePicker
                                                id="filter_date_to"
                                                format="MMM DD, YYYY"
                                                state={{ filter_date_to: filterDateTo || '' }}
                                                placeholder="Select To Date"
                                                imageClass={true}
                                                handleSelect={handleFilterDateToSelect}
                                                setdateError={() => {}}
                                                setToastErrorMessage={setLocalToastErrorMessage}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Notice type — Updates only */}
                                {!isHomework && (
                                    <div className="flex flex-col shrink-0">
                                        <p className="text-[#031624] mb-1">Type</p>
                                        <FeeStructureDropdown
                                            dropDownName="notice_type"
                                            value={noticeTypeValue}
                                            placeHolder="All Types"
                                            options={noticeTypeDropdownOptions}
                                            handleOnchangeInput={handleFilterNoticeType}
                                        />
                                    </div>
                                )}

                                <div className="flex md:col-span-2 justify-end gap-1">
                                    <button
                                        type="button"
                                        onClick={handleFetchFilters}
                                        style={{'min-width': '120px'}}
                                        disabled={loadingFilterAYs || applyingFilters || loading}
                                        className="view-actn-btn"
                                    >
                                        {loadingFilterAYs || applyingFilters || loading ? <Loader colorCode="#ffffff" /> : "Apply Filter"}
                                    </button>
                                    {!isInactiveBranch && (
                                        <button
                                            type="button"
                                            className={`prmy-btn ${isAddBlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                            onClick={handleNavigateToAdd}
                                            aria-disabled={isAddBlocked}
                                        >
                                            {isHomework ? 'Add Homework' : 'Add Update'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4">
                                {loading ? (
                                    <ItemShimmer />
                                ) : (
                                    <>
                                        {isHomework ? (
                                            <HomeworkTable
                                                data={list?.data || []}
                                                pageNumber={Number(searchParams.get('page_number')) || 1}
                                                canEdit={canEdit}
                                                canDelete={canDelete}
                                                setConfirmationState={setConfirmationState}
                                                navigate={navigate}
                                                emptyScreenMessage={emptyScreenMessage}
                                                currentQuery={searchParams.toString()}
                                            />
                                        ) : (
                                            <UpdatesTable
                                                data={list?.data || []}
                                                pageNumber={Number(searchParams.get('page_number')) || 1}
                                                canEdit={canEdit}
                                                canDelete={canDelete}
                                                setConfirmationState={setConfirmationState}
                                                navigate={navigate}
                                                emptyScreenMessage={emptyScreenMessage}
                                                currentQuery={searchParams.toString()}
                                            />
                                        )}
                                        {list?.total_count > 10 && (
                                            <Pagination
                                                TotalCount={list.total_count}
                                                totalPageNumber={Math.ceil(list.total_count / 10)}
                                                selectedPageNumber={Number(searchParams.get('page_number')) || 1}
                                                handleOnClickPageNumber={handleOnClickPageNumber}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {Object.keys(confirmationState).length > 0 && (
                <AnimatedModel
                    Children={
                        <ConfirmationPrompt
                            handleOnCLickActivate={handleDelete}
                            handleClose={() => setConfirmationState({})}
                            tittle={isHomework ? 'Delete Homework' : 'Delete Update'}
                            message={isHomework ? 'Are you sure you want to delete the homework?' : 'Are you sure you want to delete this update?'}
                            buttonName="Delete"
                            loadingActivation={loadingActivation}
                        />
                    }
                />
            )}
        </>
    );
}
